import { ClientForm } from '@/components/clients/ClientForm'
import { ClientList } from '@/components/clients/ClientList'
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { Client, ClientFormData } from '@/types/api'
import React, { useEffect, useState } from 'react'

const ClientsPage: React.FC = () => {
  const { toast } = useToast()
  const [clients, setClients] = useState<Client[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [showForm, setShowForm] = useState(false)
  const [editingClient, setEditingClient] = useState<Client | undefined>(undefined)
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const loadClients = React.useCallback(async () => {
    setIsLoading(true)
    try {
      const response = await apiClient.getClients()
      setClients(response.data || [])
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to load clients',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }, [toast])

  useEffect(() => {
    loadClients()
  }, [loadClients])

  const handleAddClient = () => {
    setEditingClient(undefined)
    setShowForm(true)
  }

  const handleEditClient = (client: Client) => {
    setEditingClient(client)
    setShowForm(true)
  }

  const handleFormSubmit = async (data: ClientFormData) => {
    setIsSubmitting(true)
    try {
      if (editingClient) {
        await apiClient.updateClient(editingClient.id, data)
        toast({ title: 'Success', description: 'Client updated successfully' })
      } else {
        await apiClient.createClient(data)
        toast({ title: 'Success', description: 'Client created successfully' })
      }
      setShowForm(false)
      loadClients()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to save client',
        variant: 'destructive',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteClient = (client: Client) => {
    setClientToDelete(client)
  }

  const confirmDelete = async () => {
    if (!clientToDelete) return
    try {
      await apiClient.deleteClient(clientToDelete.id)
      toast({ title: 'Success', description: 'Client deleted successfully' })
      setClientToDelete(null)
      loadClients()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete client',
        variant: 'destructive',
      })
    }
  }

  const handleToggleStatus = async (clientId: string) => {
    try {
      await apiClient.toggleClientStatus(clientId)
      toast({ title: 'Success', description: 'Client status updated' })
      loadClients()
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Clients</h1>
        <p className="text-muted-foreground">Manage trusted clients and partners</p>
      </div>

      {showForm ? (
        <ClientForm
          initialData={editingClient}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowForm(false)}
          isLoading={isSubmitting}
        />
      ) : (
        <ClientList
          clients={clients}
          isLoading={isLoading}
          onAddClient={handleAddClient}
          onEditClient={handleEditClient}
          onDeleteClient={(id) => {
            const client = clients.find((c) => c.id === id)
            if (client) handleDeleteClient(client)
          }}
          onToggleStatus={handleToggleStatus}
        />
      )}

      <ConfirmationDialog
        _open={!!clientToDelete}
        onOpenChange={(open) => !open && setClientToDelete(null)}
        title="Delete Client"
        description={`Are you sure you want to delete ${clientToDelete?.name}?`}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        variant="destructive"
      />
    </div>
  )
}

export default ClientsPage
